import type {
  LoginResponse,
  NonceResponse,
  RefreshResult,
  UserProfile,
} from "../types";
import { HttpClient } from "./HttpClient";

interface LoginEnvelope {
  success: boolean;
  data: {
    user: {
      walletAddress: string;
      activeStrategies: string[];
      isActive: boolean;
      referralCode: string;
      vaultVersion?: string;
    };
    // The session token is now delivered via an httpOnly Set-Cookie header
    // rather than the response body. It may be omitted entirely; when present
    // only `expiresAt` is of interest (the token value stays in the cookie).
    token?: {
      token?: string;
      expiresAt?: string;
    };
  };
}

export class AuthService {
  constructor(private readonly http: HttpClient) {}

  async getNonce(address: string): Promise<NonceResponse> {
    const response = await this.http.post<{
      success: boolean;
      data: {
        message: string;
        walletAddress: string;
        timestamp: number;
      };
    }>("/api/auth/nonce", {
      walletAddress: address,
    });

    return {
      message: response.data.message,
      walletAddress: response.data.walletAddress as `0x${string}`,
      timestamp: response.data.timestamp,
    };
  }

  async login(
    address: string,
    message: string,
    signature: string,
  ): Promise<LoginResponse> {
    const response = await this.http.post<LoginEnvelope>("/api/auth/login", {
      walletAddress: address,
      message,
      signature,
    }, {
      "X-App-ID": this.http.getAppId(),
    });
    return {
      // token now lives in an httpOnly cookie; expiry is managed server-side.
      user: {
        walletAddress: response.data.user.walletAddress as `0x${string}`,
        activeStrategies: response.data.user.activeStrategies,
        isActive: response.data.user.isActive,
        vaultVersion: response.data.user.vaultVersion ?? "",
        morphoVault: null,
        isInitialDeposit: false,
        isWhitelisted: false,
      },
    };
  }

  async refresh(): Promise<RefreshResult> {
    // Cookie-based: the current auth_token cookie is sent via credentials:"include".
    // No body or wallet signature required (Content-Type: application/json is set
    // by HttpClient). The backend revokes the old token and sets a new cookie.
    const response = await this.http.post<{
      success: boolean;
      data: { expiresAt: string };
    }>("/api/auth/refresh");
    return { expiresAt: response.data?.expiresAt ?? "" };
  }

  async getMe(): Promise<UserProfile> {
    const response = await this.http.get<any>("/api/auth/me");
    const profile: UserProfile = {
      morphoVault: response.morphoVault ?? null,
      isInitialDeposit: response.isInitialDeposit ?? false,
      isWhitelisted: Boolean(response.isWhitelisted),
      vaultVersion: response.vaultVersion ?? "",
    };
    if (response.twitterAccount !== undefined) {
      profile.twitterAccount = response.twitterAccount;
    }
    return profile;
  }
}
